import { VersionCheckResult } from 'services/version-check.dispatch';

// Pure refresh-decision helper for cs-day-items' update-available
// card. Given the backend's status + the dismissal check + the
// current shown state + the in-memory telemetry-fired set, returns
// the next card state and whether app_update.shown should fire.
// Pure so the decision table is unit-testable without instantiating
// Angular (mocha's ts-node register can't load files that
// transitively pull in @angular/core's ESM exports — same
// constraint as the VersionCheckService helpers).
//
// Mutual exclusion with the "What's new" card is not decided here. The
// boolean fields may independently be true; which of the two actually
// renders is decided in cs-day-items-notice-stack.ts, along with every
// other rule about which dashboard notice cards may appear together.
// See design-docs/in-app-update-prompt.md §Mutual Exclusion.

export interface UpdateCardDecision {
  // The new value of the notice's `wantsToShow` (`CsUpdateAvailableNotice`).
  show: boolean;
  // The new value of the notice's `availableVersion`. null when hidden.
  availableVersion: string | null;
  // Whether the caller should call
  // TelemetryService.trackAppUpdateEvent('shown', 'card', version).
  // The throttle's whole job is making this stay false after the
  // first emission per version per session.
  fireShownTelemetry: boolean;
}

export function decideUpdateCardState(
  result: VersionCheckResult,
  isDismissed: boolean,
  wasShowing: boolean,
  shownTelemetryFiredFor: ReadonlySet<string>,
  installInProgress: boolean,
  // #6770: the running build is below config/MinimumSupportedVersion. Undefined
  // reads as false, which is exactly the behaviour before #6770.
  belowMinimum?: boolean,
): UpdateCardDecision {
  // if no update from the backend → hide the card, no telemetry.
  // this is the steady-state answer on a freshly-installed build
  // and on unsupported platforms (VersionCheckService returns
  // updateAvailable:false there).
  if (!result.updateAvailable) {
    return { show: false, availableVersion: null, fireShownTelemetry: false };
  }
  // if dismissed within the 24h window → hide. dismissal is keyed
  // by availableVersion so a newer version re-surfaces independently.
  // EXCEPTION: a flexible update already downloading or downloaded
  // (installInProgress) overrides the dismissal — the card must stay
  // visible so the user can reach the Restart action, otherwise a
  // downloaded update started from Settings after an earlier card
  // dismissal would be stranded with no in-app way to finish it.
  // EXCEPTION 2 (#6770): a build below the minimum supported version ignores the
  // dismissal too — its card has no X, and a dismissal from before the minimum was
  // raised must not hide it.
  if (isDismissed && !installInProgress && !belowMinimum) {
    return { show: false, availableVersion: null, fireShownTelemetry: false };
  }
  // shown for the first time this session for this version → fire
  // app_update.shown. wasShowing covers "the card is already
  // visible and we're just re-confirming on a resume" (no
  // re-emission). the Set covers "the card was visible, was
  // dismissed (or otherwise hidden), and is now coming back" — same
  // session, same version, suppress. We also suppress while an update
  // is in progress: a downloading/restart card is not the "an update
  // is available" nudge that `shown` measures.
  const fireShownTelemetry =
    !installInProgress && !wasShowing && !shownTelemetryFiredFor.has(result.availableVersion);
  return {
    show: true,
    availableVersion: result.availableVersion,
    fireShownTelemetry,
  };
}
